import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Chip,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Avatar,
  ButtonGroup,
  Divider,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  AccountTree as PedigreeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  ViewList as ViewListIcon,
  GridView as GridViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DatabaseService from '../services/database';
import { filterBirdsBySearchAndSex, getBirdListLabels } from '../utils/birdSearch';
import { sexAvatarSx } from '../utils/sexStyle';

// The chosen layout sticks between visits — switching back to your preferred
// view every time you open the loft would get old fast.
const VIEW_MODE_KEY = 'pigeonPedigree.birdListView';

const readStoredViewMode = () => {
  try {
    return window.localStorage.getItem(VIEW_MODE_KEY) === 'grid' ? 'grid' : 'list';
  } catch (error) {
    return 'list';
  }
};

const BirdList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [birds, setBirds] = useState([]);
  const [filteredBirds, setFilteredBirds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sexFilter, setSexFilter] = useState('all'); // 'all', 'male', 'female'
  const [viewMode, setViewMode] = useState(readStoredViewMode); // 'list', 'grid'
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBird, setSelectedBird] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Phones already get a stacked card row, and there isn't width for a useful
  // grid — the toggle is a desktop affordance only.
  const showGrid = viewMode === 'grid' && !isMobile;

  useEffect(() => {
    loadBirds();
  }, []);

  useEffect(() => {
    filterBirds();
  }, [searchTerm, sexFilter, birds]);

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
    } catch (error) {
      // Remembering the layout is a nicety; storage being unavailable is not fatal.
    }
  }, [viewMode]);

  const loadBirds = async () => {
    try {
      const birdsData = await DatabaseService.getAllBirds();
      const birdList = Array.isArray(birdsData) ? birdsData : [];
      setBirds(birdList);
      setFilteredBirds(birdList);
    } catch (error) {
      console.error('Error loading birds:', error);
      setBirds([]);
      setFilteredBirds([]);
    } finally {
      setLoading(false);
    }
  };

  const filterBirds = () => {
    setFilteredBirds(filterBirdsBySearchAndSex(birds, { searchTerm, sexFilter }));
  };

  const handleMenuOpen = (event, bird) => {
    setAnchorEl(event.currentTarget);
    setSelectedBird(bird);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBird(null);
  };

  const handleViewPedigree = () => {
    if (selectedBird) {
      navigate(`/pedigree/${selectedBird.id}`);
    }
    handleMenuClose();
  };

  const handleEditBird = () => {
    if (selectedBird) {
      navigate(`/birds/${selectedBird.id}/edit`);
    }
    handleMenuClose();
  };

  const handleDeleteBird = () => {
    setDeleteDialog(true);
    setAnchorEl(null);
  };

  const confirmDelete = async () => {
    if (selectedBird) {
      try {
        await DatabaseService.deleteBird(selectedBird.id);
        await loadBirds();
        setDeleteDialog(false);
        setSelectedBird(null);
      } catch (error) {
        console.error('Error deleting bird:', error);
        setDeleteDialog(false);
        if (error.isLicenseError) {
          window.alert(error.message);
        }
      }
    }
  };

  const BirdListItem = ({ bird }) => {
    const { displayName, ringLabel } = getBirdListLabels(bird);

    // Small screens get a stacked card layout: identity row (avatar + name +
    // menu), details line, then full-width action buttons. The desktop row
    // below is untouched — its secondaryAction layout needs the width that
    // phones don't have.
    if (isMobile) {
      return (
        <>
          <ListItem
            onClick={() => navigate(`/birds/${bird.id}`)}
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, display: 'block', py: 2 }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar
                src={bird.body_photo}
                sx={{ width: 42, height: 42, flexShrink: 0, ...sexAvatarSx(bird.sex) }}
              >
                {bird.sex === 'male' ? <MaleIcon /> : <FemaleIcon />}
              </Avatar>
              <Box flexGrow={1} minWidth={0}>
                <Typography variant="body1" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                  {displayName}
                </Typography>
                {ringLabel && (
                  <Chip label={ringLabel} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                )}
              </Box>
              <IconButton
                edge="end"
                onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, bird); }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
            {(bird.breed || bird.color) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {[bird.breed && `Strain: ${bird.breed}`, bird.color && `Color: ${bird.color}`]
                  .filter(Boolean)
                  .join('  ·  ')}
              </Typography>
            )}
            <Box display="flex" gap={1} mt={1.5}>
              <Button
                size="small"
                variant="outlined"
                fullWidth
                startIcon={<PedigreeIcon />}
                onClick={(e) => { e.stopPropagation(); navigate(`/pedigree/${bird.id}`); }}
              >
                Pedigree
              </Button>
              <Button
                size="small"
                variant="outlined"
                fullWidth
                startIcon={<EditIcon />}
                onClick={(e) => { e.stopPropagation(); navigate(`/birds/${bird.id}/edit`); }}
              >
                Edit
              </Button>
            </Box>
          </ListItem>
          <Divider component="li" />
        </>
      );
    }

    return (
      <>
        <ListItem
          sx={{
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
            py: 2
          }}
          onClick={() => navigate(`/birds/${bird.id}`)}
          secondaryAction={
            <Box display="flex" gap={1}>
              <Button
                size="small"
                startIcon={<PedigreeIcon />}
                onClick={(e) => { e.stopPropagation(); navigate(`/pedigree/${bird.id}`); }}
              >
                Pedigree
              </Button>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={(e) => { e.stopPropagation(); navigate(`/birds/${bird.id}/edit`); }}
              >
                Edit
              </Button>
              <IconButton
                edge="end"
                onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, bird); }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
          }
        >
          <ListItemAvatar>
            <Avatar
              src={bird.body_photo}
              sx={{ width: 42, height: 42, ...sexAvatarSx(bird.sex) }}
            >
              {bird.sex === 'male' ? <MaleIcon /> : <FemaleIcon />}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1" component="span" fontWeight="bold">
                  {displayName}
                </Typography>
                {ringLabel && (
                  <Chip label={ringLabel} size="small" variant="outlined" />
                )}
              </Box>
            }
            secondary={
              <Box component="span" display="flex" flexDirection="column" gap={0.5} mt={1}>
                {bird.breed && (
                  <Typography variant="body2" color="text.secondary">
                    Strain: {bird.breed}
                  </Typography>
                )}
                {bird.color && (
                  <Typography variant="body2" color="text.secondary">
                    Color: {bird.color}
                  </Typography>
                )}
              </Box>
            }
            sx={{ pr: 30 }}
          />
        </ListItem>
        <Divider component="li" />
      </>
    );
  };

  // Grid tile: the photo leads, so a loft can be scanned by eye rather than by
  // reading every row.
  const BirdGridCard = ({ bird }) => {
    const { displayName, ringLabel } = getBirdListLabels(bird);
    const tint = sexAvatarSx(bird.sex);
    const details = [bird.breed && `Strain: ${bird.breed}`, bird.color && `Color: ${bird.color}`]
      .filter(Boolean);

    return (
      <Card
        onClick={() => navigate(`/birds/${bird.id}`)}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          transition: 'box-shadow 140ms ease, transform 140ms ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 2px 4px rgba(20,30,55,0.06), 0 10px 22px -6px rgba(20,30,55,0.16)'
          }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            pt: '72%',
            bgcolor: tint.bgcolor,
            color: tint.color,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          {bird.body_photo ? (
            <Box
              component="img"
              src={bird.body_photo}
              alt={displayName}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': { fontSize: 56, opacity: 0.65 }
              }}
            >
              {bird.sex === 'male' ? <MaleIcon /> : <FemaleIcon />}
            </Box>
          )}
          <IconButton
            size="small"
            aria-label="Bird actions"
            onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, bird); }}
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              bgcolor: 'rgba(255,255,255,0.92)',
              boxShadow: '0 1px 3px rgba(20,30,55,0.18)',
              '&:hover': { bgcolor: '#fff' }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        <CardContent sx={{ p: 2, pb: 1, flexGrow: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" noWrap title={displayName}>
            {displayName}
          </Typography>
          {ringLabel && (
            <Chip label={ringLabel} size="small" variant="outlined" sx={{ mt: 0.75, maxWidth: '100%' }} />
          )}
          {details.length > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}
              title={details.join('  ·  ')}
            >
              {details.join('  ·  ')}
            </Typography>
          )}
        </CardContent>

        <Box display="flex" gap={1} px={2} pb={2}>
          <Button
            size="small"
            variant="outlined"
            fullWidth
            startIcon={<PedigreeIcon />}
            onClick={(e) => { e.stopPropagation(); navigate(`/pedigree/${bird.id}`); }}
          >
            Pedigree
          </Button>
          <Button
            size="small"
            variant="outlined"
            fullWidth
            startIcon={<EditIcon />}
            onClick={(e) => { e.stopPropagation(); navigate(`/birds/${bird.id}/edit`); }}
          >
            Edit
          </Button>
        </Box>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading birds...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          All Birds ({filteredBirds.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/birds/add')}
          size="large"
        >
          Add New Bird
        </Button>
      </Box>

      {/* Search and Filters */}
      <Box 
        mb={4} 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <TextField
          placeholder="Search birds by name, ring details, breed, or color..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: { xs: 0, sm: 500 }, maxWidth: 800, width: { xs: '100%', sm: 'auto' } }}
        />
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <ButtonGroup variant="outlined">
            <Button
              variant={sexFilter === 'all' ? 'contained' : 'outlined'}
              onClick={() => setSexFilter('all')}
            >
              All
            </Button>
            <Button
              variant={sexFilter === 'male' ? 'contained' : 'outlined'}
              startIcon={<MaleIcon />}
              onClick={() => setSexFilter('male')}
              color="info"
            >
              Males
            </Button>
            <Button
              variant={sexFilter === 'female' ? 'contained' : 'outlined'}
              startIcon={<FemaleIcon />}
              onClick={() => setSexFilter('female')}
              color="secondary"
            >
              Females
            </Button>
          </ButtonGroup>

          {!isMobile && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, next) => { if (next) setViewMode(next); }}
              aria-label="Bird layout"
            >
              <ToggleButton value="list" aria-label="List view">
                <Tooltip title="List view">
                  <ViewListIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="grid" aria-label="Grid view">
                <Tooltip title="Grid view">
                  <GridViewIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>

      {/* Birds List */}
      {filteredBirds.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm || sexFilter !== 'all' ? 'No birds found matching your filters' : 'No birds added yet'}
          </Typography>
          {!searchTerm && sexFilter === 'all' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/birds/add')}
              sx={{ mt: 2 }}
            >
              Add Your First Bird
            </Button>
          )}
        </Box>
      ) : showGrid ? (
        <Grid container spacing={2.5}>
          {filteredBirds.map((bird) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={bird.id}>
              <BirdGridCard bird={bird} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
          {filteredBirds.map((bird) => (
            <BirdListItem key={bird.id} bird={bird} />
          ))}
        </List>
      )}

      {/* Context Menu */}
      <Dialog
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            minWidth: '200px',
            padding: '8px',
            borderRadius: '5px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle>Bird Actions</DialogTitle>
        <DialogContent>
          <List>
            <ListItem button onClick={handleViewPedigree}>
              <ListItemIcon><PedigreeIcon /></ListItemIcon>
              <ListItemText primary="View Preview Pedigree" />
            </ListItem>
            <ListItem button onClick={handleEditBird}>
              <ListItemIcon><EditIcon /></ListItemIcon>
              <ListItemText primary="Edit Bird" />
            </ListItem>
            <ListItem button onClick={handleDeleteBird} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
              <ListItemText primary="Delete Bird" />
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Bird</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedBird?.name || selectedBird?.ring_number || 'Unnamed Bird'}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BirdList;
